import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  deletePropertyDeveloper,
  fetchPropertyManagersByPropertyId,
} from 'src/services/client';
import {
  PropertyManagerContact,
  PropertyManagerRow,
} from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/types';
import { toPropertyManagerRow } from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/usePropertyDevelopersListOperations.helpers';

export interface UsePropertyDevelopersListOperationsReturn {
  propertyDevelopers: PropertyManagerRow[];
  isFetchingDevelopers: boolean;
  fetchPropertyDevelopers: (propertyId: string) => Promise<void>;

  isDeleting: boolean;
  deletePropertyDeveloperFromProperty: (
    contactId: any,
    propertyId: any
  ) => Promise<{ success: boolean; error?: any }>;
}

/* Read/delete surface for the Property Developers table on the property page.
 * Split out of the old useContactAPIOperationsProperties god hook so the
 * parent page and the child table don't double-instantiate a single hook
 * mixing read + write state. Write operations live in the orchestrator
 * (useContactAPIOperationsProperties), which composes useContactAPI. */
export const usePropertyDevelopersListOperations =
  (): UsePropertyDevelopersListOperationsReturn => {
    const [propertyDevelopers, setPropertyDevelopers] = useState<
      PropertyManagerRow[]
    >([]);
    const [isFetchingDevelopers, setIsFetchingDevelopers] =
      useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const fetchPropertyDevelopers = async (
      propertyId: string
    ): Promise<void> => {
      setIsFetchingDevelopers(true);

      try {
        const response: any =
          await fetchPropertyManagersByPropertyId(propertyId);

        if (response.status === 200 || response.ok) {
          const data: PropertyManagerContact[] = await response.json();
          const rows: PropertyManagerRow[] = data.map(
            (item: PropertyManagerContact) => toPropertyManagerRow(item)
          );

          setPropertyDevelopers(rows);
        } else {
          console.error(
            'Failed to fetch property developers:',
            response.status
          );
          toast.error(
            '🚫 Failed to load property developers. Please refresh the page.',
            { autoClose: 8000, position: 'top-center' }
          );
        }
      } catch (error) {
        console.error('Error fetching property developers:', error);
        toast.error(
          '🚫 Failed to load property developers. Please refresh the page.',
          { autoClose: 8000, position: 'top-center' }
        );
      } finally {
        setIsFetchingDevelopers(false);
      }
    };

    const deletePropertyDeveloperFromProperty = async (
      contactId: any,
      propertyId: any
    ): Promise<{ success: boolean; error?: any }> => {
      setIsDeleting(true);

      try {
        const response: any = await deletePropertyDeveloper(
          contactId,
          propertyId
        );

        let data: any = {};

        if (
          response.headers.get('content-type')?.includes('application/json')
        ) {
          data = await response.json();
        }

        // Endpoint occasionally returns 200 with an error body — inspect the body too.
        if (data.statusCode === 403 || data.statusCode >= 400) {
          toast.error(
            '🚫 We were unable to delete the contact due to a temporary server issue. Please try again shortly. ' +
              'If this issue persists please Contact Juniper support',
            { autoClose: 12000, position: 'top-center' }
          );
          return { success: false, error: data.message };
        }

        if (!response.ok && response.status !== 200) {
          toast.error(
            '🚫 Something went wrong with deleting a contact, please try again.',
            { autoClose: 12000, position: 'top-center' }
          );
          return { success: false, error: 'Request failed' };
        }

        toast.success('ⓘ The user has been deleted.', {
          autoClose: 10000,
          position: 'top-center',
        });

        return { success: true };
      } catch (error) {
        console.error('Error deleting property developer:', error);
        toast.error('🚫 Something went wrong, please try again.', {
          autoClose: 12000,
          position: 'top-center',
        });
        return { success: false, error };
      } finally {
        setIsDeleting(false);
      }
    };

    return {
      propertyDevelopers,
      isFetchingDevelopers,
      fetchPropertyDevelopers,
      isDeleting,
      deletePropertyDeveloperFromProperty,
    };
  };
